import {type Moment} from 'moment-timezone';
import i18next from 'i18next';

import {hasValue} from '@famly/stat_ts-utils_has-value';

import {getClosestPeriodStartDate} from './generate-edit-plan-date-range-options';
import type * as Types from '../types';

/**
 * Determines the initial plan date range based on the initial start date type
 * @param initialStartDate The initial start date information
 * @param initialPlan The initial plan data
 * @param biweeklyCadenceStartDate Optional biweekly cadence start date
 * @param selectedChildren Optional array of selected children (for error generation)
 * @returns An object containing the plan date range and any children errors that are displayed in the billing plan modal (Chill bill)
 */
export const defaultPlanDateRange = ({
    initialStartDate,
    initialPlan,
    biweeklyCadenceStartDate,
    selectedChildren,
}: {
    initialStartDate: Types.InitialStartDate;
    initialPlan: Types.Plan;
    biweeklyCadenceStartDate: Moment;
    selectedChildren: Array<Types.Child>;
}): {
    planDateRange: {startDate: string; endDate: string | null};
    childrenErrors: undefined | Array<Types.FullPlanBilledError>;
} => {
    switch (initialStartDate.type) {
        case 'full-plan-billed': {
            return {
                planDateRange: {
                    startDate: initialPlan.startDate.format('YYYY-MM-DD'),
                    endDate: initialPlan.endDate?.format('YYYY-MM-DD') ?? null,
                },
                childrenErrors: [
                    {
                        type: 'full-plan-billed',
                        childIds: selectedChildren.map(child => child.id),
                        message: i18next.t(
                            'react.simplifiedUsBillingPlan.translations.json.childrenErrors.fullPlanBilled',
                        ),
                    },
                ],
            };
        }
        case 'current-plan-effective-date': {
            const periodStartDate = getClosestPeriodStartDate(
                initialStartDate.date,
                initialPlan.billingFrequency,
                biweeklyCadenceStartDate,
            );
            const startDate = periodStartDate.moment.isAfter(initialPlan.endDate)
                ? initialStartDate.date.format('YYYY-MM-DD')
                : periodStartDate.text;
            const endDate =
                hasValue(initialPlan.endDate) && initialStartDate.date.isAfter(initialPlan.endDate)
                    ? null
                    : initialPlan.endDate?.format('YYYY-MM-DD');

            return {
                planDateRange: {
                    startDate,
                    endDate: endDate ?? null,
                },
                childrenErrors: undefined,
            };
        }
        default: {
            return {
                planDateRange: {
                    startDate: initialPlan.startDate.format('YYYY-MM-DD'),
                    endDate: initialPlan.endDate?.format('YYYY-MM-DD') ?? null,
                },
                childrenErrors: undefined,
            };
        }
    }
};
